'use client';
import { ShieldCheck } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import type {
  ElasticBranch,
  ElasticBranchResellerPackagingOption,
} from '@//app/api/search/branch/[id]';
import type { ElasticBranding } from '@//app/api/search/brandings';
import { Alert } from '@//components/Alert';
import { Button } from '@//components/Button';
import { Grid } from '@//components/Grid';
import { Logo } from '@//components/Logo';
import { markdownToHtml } from '@//utils/text';
import styles from './Guarantees.module.scss';
import type { GuaranteeItem } from './utils';

const GuaranteesModal = lazy(() => import('./GuaranteesModal'));

interface GuaranteesProps {
  manufacturerPackaging?: ElasticBranding;
  resellerPackagings?: ElasticBranchResellerPackagingOption[];
  branch: ElasticBranch;
}

export const Guarantees: React.FC<GuaranteesProps> = ({
  manufacturerPackaging,
  resellerPackagings,
  branch,
}) => {
  const t = useTranslations('pages.item.moreInformationSection.guarantees');
  const [guaranteeModal, setGuaranteeModal] = useState<GuaranteeItem | undefined>();
  const onOpenGuaranteeModal = useCallback(
    (guarantee: GuaranteeItem) => setGuaranteeModal(guarantee),
    [],
  );
  const onCloseGuaranteeModal = useCallback(() => setGuaranteeModal(undefined), []);

  const guarantees = useMemo((): GuaranteeItem[] => {
    const items: GuaranteeItem[] = [];

    if (manufacturerPackaging) {
      items.push({
        title: manufacturerPackaging.name,
        description: markdownToHtml(manufacturerPackaging.description),
        logoUrl: manufacturerPackaging.logo.file.url,
        type: 'manufacturerPackaging',
        link: manufacturerPackaging.website
          ? {
              href: manufacturerPackaging.website,
              title: t('readMore'),
            }
          : undefined,
      });
    }
    if (resellerPackagings && resellerPackagings.length > 0) {
      resellerPackagings.forEach((packaging) => {
        items.push({
          title: packaging.title,
          description: packaging.description,
          imageUrl: packaging.imageUrl,
          logoUrl: branch.logo,
          type: 'resellerPackaging',
          link: packaging.ctaUrl
            ? {
                href: packaging.ctaUrl,
                title: packaging.ctaName || t('readMore'),
              }
            : undefined,
        });
      });
    }
    return items;
  }, [manufacturerPackaging, resellerPackagings]);

  return (
    <>
      <Suspense fallback={null}>
        <GuaranteesModal guarantee={guaranteeModal} onClose={onCloseGuaranteeModal} />
      </Suspense>
      {guarantees.length > 0 ? (
        <Grid columns={2} equalGap>
          {guarantees.map((guarantee, index) => (
            <article
              key={`guarantee-card-${index}`}
              className={styles.box}
              aria-labelledby={`guarantee-${guarantee.type}-${index}-heading`}
              data-testid={`guarantee-${guarantee.type}-${index}`}
            >
              <div className={styles.content}>
                {guarantee.logoUrl ? (
                  <Logo
                    src={`${guarantee.logoUrl}?w=120`}
                    srcSet={`${guarantee.logoUrl}?w=240 2x`}
                    alt={guarantee.title}
                    width={120}
                    height={48}
                    logoSize="md"
                    className={styles.logo}
                  />
                ) : (
                  <ShieldCheck aria-hidden="true" />
                )}
                <h3 className={styles.heading} id={`guarantee-${guarantee.type}-${index}-heading`}>
                  {guarantee.title}
                </h3>
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{
                    __html: guarantee.description,
                  }}
                />
              </div>
              {guarantee.link && (
                <Button
                  variant="secondary"
                  sizeLtMd="sm"
                  sizeMd="md"
                  title={t('readMoreTitle')}
                  className={styles.action}
                  onClick={() => onOpenGuaranteeModal(guarantee)}
                >
                  {t('readMore')}
                </Button>
              )}
            </article>
          ))}
        </Grid>
      ) : (
        <Alert id="alert-no-guarantees" variant="info">
          {t('noGuarantees')}
        </Alert>
      )}
    </>
  );
};
